import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import {
  readApplySession,
  patchResumeDraft,
  setResumeFileMeta,
  markHubStage,
  saveResumeStageAfterBasic,
  clearApplySession,
  applicationFormPath,
} from '../../lib/screeningApplySession';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import {
  ArrowRightIcon,
  CheckIcon,
  LinkIcon,
  UserIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  AcademicCapIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useScreeningExpiryRedirect } from '../../hooks/useScreeningExpiryRedirect';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import ResumeProcessingOverlay from '../../components/screenings/ResumeProcessingOverlay';
import { useCandidateLanguage } from '../../i18n/candidate';

// English text: ResumeProcessingOverlay translates the title and each message when it shows them.
const PROCESSING_MESSAGES = [
  'Extracting text from document…',
  'Analyzing resume content…',
  'Parsing candidate information…',
  'Running AI evaluation…',
  'Almost there, finalizing details…',
];

const STEP_SECONDARY = {
  'Basic details': 'Share your contact information',
  'Resume upload': 'Upload your latest resume',
  'Additional details': 'Share notice period and compensation',
  Assessment: 'Complete skills assessment',
  'AI interview': 'Answer structured questions',
  'Document upload': 'Upload supporting documents (optional)',
};

const getStepMeta = (interviewType) => ({
  'Basic details': { icon: UserIcon, desc: '', gradient: 'from-blue-500 to-indigo-600' },
  'Resume upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Additional details': { icon: ClockIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  Assessment: { icon: AcademicCapIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'AI interview': {
    icon: interviewType === 'video' ? VideoCameraIcon : MicrophoneIcon,
    desc: '',
    gradient: 'from-electric-blue to-indigo-600',
  },
  'Document upload': { icon: DocumentTextIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
  'Application form': { icon: ClipboardDocumentListIcon, desc: '', gradient: 'from-electric-blue to-indigo-600' },
});

// Mobile/flaky connections can drop a large multipart upload mid-flight, which axios
// surfaces as a generic "Network Error" with no `response` (as opposed to a server
// error, which does have one). Retry only that case — retrying a real server error
// (e.g. validation failure) would just resubmit a request we already know is bad.
//
// The timeout is intentionally long: when journey automation is enabled, the server
// scores the resume synchronously (OCR on scanned/image resumes can take minutes)
// before responding, so a short client timeout would abort an upload that was still
// legitimately processing — matches the server's extended timeout for this route
// (see the /apply timeout rule in server/index.js).
const UPLOAD_TIMEOUT_MS = 600_000;
const UPLOAD_MAX_RETRIES = 2;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Safely compresses image files (e.g. 2MB+ camera photos) on canvas to < 800KB.
 */
function compressImageBuffer(buffer, mimeType) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return resolve(null);
    }
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      const MAX_DIM = 1600;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width > height) {
          height = Math.round((height * MAX_DIM) / width);
          width = MAX_DIM;
        } else {
          width = Math.round((width * MAX_DIM) / height);
          height = MAX_DIM;
        }
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (outBlob) => resolve(outBlob),
        'image/jpeg',
        0.82
      );
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * Safely materializes any File (including Google Drive / iCloud / OneDrive virtual content:// stream pointers)
 * into a solid in-memory Blob. Forces Android/iOS to finish downloading the cloud file into RAM before HTTP dispatch.
 */
async function materializeFileToBlob(file) {
  if (!file) return null;
  if (file.size === 0) {
    throw new Error('FILE_EMPTY_0_BYTES');
  }

  // Read file into an ArrayBuffer (forces OS to finish cloud download from Google Drive/iCloud into RAM)
  let buffer;
  if (typeof file.arrayBuffer === 'function') {
    buffer = await file.arrayBuffer();
  } else {
    buffer = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('FILE_READ_FAILED'));
      reader.readAsArrayBuffer(file);
    });
  }

  if (!buffer || buffer.byteLength === 0) {
    throw new Error('FILE_EMPTY_0_BYTES');
  }

  const mimeType = file.type || 'application/pdf';

  // If it's a large camera photo image (> 1 MB), compress it via canvas
  if (/^image\/(jpeg|jpg|png)$/i.test(mimeType) && buffer.byteLength > 1024 * 1024) {
    try {
      const compressedBlob = await compressImageBuffer(buffer, mimeType);
      if (compressedBlob && compressedBlob.size > 0 && compressedBlob.size < buffer.byteLength) {
        return compressedBlob;
      }
    } catch (compErr) {
      console.warn('[APPLY_UPLOAD] Image compression skipped, using original buffer', compErr);
    }
  }

  return new Blob([buffer], { type: mimeType });
}

async function postMultipartWithRetry(url, formData, logContext, onAttemptFailure) {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const attemptStartedAt = Date.now();
    // How far the body actually got before it died. This is the measurement
    // that separates "the connection dropped mid-body" from "the request never
    // left the device" from "the server rejected it" — three failures that look
    // identical to the candidate and, until now, identical in our logs too.
    let lastLoadedBytes = 0;
    let totalBytes = 0;
    let lastProgressAt = Date.now();
    let firstByteAt = 0;
    let stallReported = false;
    // Snapshot per iteration: `attempt` is mutated by the retry loop, and the
    // stall callback below outlives the iteration that created it.
    const attemptNo = attempt;
    // Reports a stall *while it is happening*. Every other event here fires
    // after the request has already resolved one way or the other — but a
    // socket that dies silently mid-body may take the full 10-minute timeout to
    // resolve, or never resolve at all if the tab is closed first. This is the
    // only signal that survives that case, and it carries the byte offset where
    // progress stopped, which is what distinguishes a mid-body death from a
    // request that never started.
    const stallTimer = setInterval(() => {
      const idleMs = Date.now() - lastProgressAt;
      if (idleMs < 20_000 || stallReported) return;
      stallReported = true;
      if (typeof onAttemptFailure === 'function') {
        try {
          onAttemptFailure({
            ...logContext,
            attempt: attemptNo,
            stalled: true,
            idleMs,
            sentBytes: lastLoadedBytes,
            totalBytes,
            sentPercent: totalBytes ? Math.round((lastLoadedBytes / totalBytes) * 100) : null,
            neverStarted: lastLoadedBytes === 0,
            wentOffline: typeof navigator !== 'undefined' ? !navigator.onLine : null,
            errorCode: 'STALLED',
            errorMessage: `No upload progress for ${Math.round(idleMs / 1000)}s`,
          });
        } catch (_) {
          /* diagnostics must never break the upload */
        }
      }
    }, 5_000);
    stallTimer.unref?.();
    try {
      const res = await axios.post(url, formData, {
        // Deliberately NO explicit Content-Type. The browser must set it so it
        // can append the multipart boundary:
        //   multipart/form-data; boundary=----WebKitFormBoundaryXyZ
        // Hard-coding 'multipart/form-data' produces a header with no boundary,
        // which makes the body unparseable. Lenient engines (desktop Chrome,
        // Safari) silently overwrite it and add the boundary, which is why this
        // worked for most candidates; stricter/privacy-hardened builds (Brave,
        // proxy and in-app browsers) do not, and reject the request locally —
        // surfacing as ERR_NETWORK with sentBytes=0 in ~35ms, i.e. the random
        // device-specific "We couldn't reach the server" on resume upload.
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (evt) => {
          lastLoadedBytes = evt.loaded || 0;
          totalBytes = evt.total || 0;
          lastProgressAt = Date.now();
          if (!firstByteAt && lastLoadedBytes > 0) firstByteAt = Date.now();
        },
      });
      clearInterval(stallTimer);
      if (attempt > 0) {
        console.info('[APPLY_UPLOAD] Succeeded after retry', {
          ...logContext,
          attempt,
          durationMs: Date.now() - attemptStartedAt,
        });
      }
      return res;
    } catch (err) {
      clearInterval(stallTimer);
      const isNetworkFailure = !err.response;
      const durationMs = Date.now() - attemptStartedAt;
      const errInfo = {
        ...logContext,
        attempt,
        durationMs,
        isNetworkFailure,
        isTimeout: err.code === 'ECONNABORTED',
        errorCode: err.code,
        errorMessage: err.message,
        httpStatus: err.response?.status,
        responseBody: err.response?.data,
        // Progress at the moment of death, plus the throughput it implies.
        // A body that stalled at 40% after 90s says something very different
        // from one that never sent a byte, and neither is a guess.
        sentBytes: lastLoadedBytes,
        totalBytes,
        sentPercent: totalBytes ? Math.round((lastLoadedBytes / totalBytes) * 100) : null,
        uplinkKbps: durationMs > 0 ? Math.round((lastLoadedBytes * 8) / durationMs) : null,
        wentOffline: typeof navigator !== 'undefined' ? !navigator.onLine : null,
        // Zero bytes ever sent means the request never got out of the device —
        // a DNS, TLS, proxy or captive-portal problem, categorically different
        // from a body that started and then died partway.
        neverStarted: lastLoadedBytes === 0,
        msToFirstByte: firstByteAt ? firstByteAt - attemptStartedAt : null,
        msSinceLastProgress: Date.now() - lastProgressAt,
        stalledEarlier: stallReported,
      };

      if (typeof onAttemptFailure === 'function') {
        try {
          onAttemptFailure(errInfo);
        } catch (_) {
          /* diagnostics must never break the upload */
        }
      }

      if (!isNetworkFailure || attempt >= UPLOAD_MAX_RETRIES) {
        console.error('[APPLY_UPLOAD] Failed, giving up', errInfo);
        Sentry.captureException(err, { extra: errInfo, tags: { feature: 'resume-apply-upload' } });
        throw err;
      }

      console.warn('[APPLY_UPLOAD] Network failure, retrying', errInfo);
      attempt += 1;
      await sleep(attempt === 1 ? 800 : attempt === 2 ? 1800 : 3000);
    }
  }
}

/**
 * Everything the browser can tell us about this device's connection, right now.
 *
 * `navigator.connection` is Chromium-only, which is fine: the candidates hitting
 * this are on Android Chrome and Android WebViews. Where it is missing the keys
 * are simply absent rather than guessed at.
 */
function readConnectionInfo() {
  if (typeof navigator === 'undefined') return {};
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const info = { online: navigator.onLine };
  if (!c) return info;
  if (c.effectiveType) info.effectiveType = c.effectiveType; // '4g' | '3g' | '2g' | 'slow-2g'
  if (typeof c.downlink === 'number') info.downlinkMbps = c.downlink;
  if (typeof c.rtt === 'number') info.rttMs = c.rtt;
  if (typeof c.saveData === 'boolean') info.saveData = c.saveData;
  if (c.type) info.netType = c.type; // 'wifi' | 'cellular' | ...
  return info;
}

/**
 * Names the browsing context, because "which app was this opened in" is one of
 * the few things that reliably separates candidates who succeed from ones who
 * do not, and a raw UA string is painful to eyeball in a log search.
 */
function readContextInfo() {
  if (typeof navigator === 'undefined') return {};
  const ua = navigator.userAgent || '';
  const info = {};
  // Android WebViews advertise themselves with `; wv)`. In-app browsers
  // (WhatsApp, Instagram, Facebook) are the usual hosts and each has its own
  // quirks around the file picker.
  if (/\bwv\b/.test(ua) || /Version\/[\d.]+ Chrome/.test(ua)) info.webview = true;
  const inApp = ua.match(/(FBAN|FBAV|Instagram|Line|WhatsApp|Snapchat|Twitter|MicroMessenger|HeyTapBrowser|OppoBrowser|SamsungBrowser|MiuiBrowser)/i);
  if (inApp) info.inApp = inApp[1];
  if (typeof window !== 'undefined') {
    info.viewport = `${window.innerWidth}x${window.innerHeight}`;
    if (window.screen) info.screen = `${window.screen.width}x${window.screen.height}`;
    // A standalone display-mode means the link was opened from a home-screen
    // shortcut, which behaves differently again.
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) info.standalone = true;
  }
  if (navigator.deviceMemory) info.deviceMemoryGB = navigator.deviceMemory;
  if (navigator.hardwareConcurrency) info.cores = navigator.hardwareConcurrency;
  return info;
}

/**
 * Which browser APIs this device actually has.
 *
 * The page assumes a modern browser throughout — FormData, Blob.arrayBuffer,
 * DataTransfer, sendBeacon, localStorage. On an old Android WebView any of
 * these can be missing or throw, and the failure surfaces as an unrelated
 * symptom (a dead upload box, a bounce to step one) with no clue as to why.
 * Recording the capability set once per page view means we can compare a
 * failing device against a working one directly, instead of theorising.
 */
function readCapabilityInfo() {
  const caps = {};
  try {
    caps.hasFormData = typeof FormData !== 'undefined';
    caps.hasDataTransfer = typeof DataTransfer !== 'undefined';
    caps.hasBeacon = typeof navigator !== 'undefined' && !!navigator.sendBeacon;
    caps.hasFileArrayBuffer = typeof Blob !== 'undefined' && typeof Blob.prototype.arrayBuffer === 'function';
    caps.hasAbortController = typeof AbortController !== 'undefined';
    // localStorage is the one that bites hardest: it throws (not returns null)
    // in some WebViews and in private mode, and this page depends on it to
    // recover basicDetails after a refresh.
    try {
      const k = '__tc_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      caps.localStorage = 'ok';
    } catch (lsErr) {
      caps.localStorage = `blocked:${lsErr?.name || 'error'}`;
    }
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      caps.storageApi = true;
    }
    caps.cookiesEnabled = typeof navigator !== 'undefined' ? navigator.cookieEnabled : null;
    if (typeof Intl !== 'undefined') {
      caps.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  } catch (_) {
    /* capability probing must never break the page */
  }
  return caps;
}

/**
 * Fire-and-forget diagnostic report.
 *
 * Uses sendBeacon when the page may be going away — a normal XHR is cancelled
 * on unload, which would lose exactly the reports that matter most (the
 * candidate who closed the tab mid-upload). Falls back to axios elsewhere.
 * Never throws and never blocks: a candidate must never be held up, or shown an
 * error, because telemetry failed.
 */
function reportDiag(screeningId, event, detail, identity, { beacon = false } = {}) {
  if (!screeningId) return;
  try {
    const payload = {
      event,
      email: identity?.email || '',
      name: identity?.name || '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      detail: { ...readConnectionInfo(), ...readContextInfo(), ...detail },
    };
    const url = `/api/screenings/${screeningId}/apply-diag`;
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    axios.post(url, payload).catch(() => {
      /* diagnostics only — never block the candidate on this */
    });
  } catch (_) {
    /* diagnostics must never break the page */
  }
}

function getInterviewStepLabel(interviewType) {
  if (interviewType === 'video') return 'AI video interview';
  if (interviewType === 'voice' || interviewType === 'audio') return 'AI voice interview';
  return 'AI interview';
}

const EMPTY_RESUME = { name: '', sizeBytes: 0, url: '', status: 'idle' };

/**
 * Owns the resume upload for this page.
 *
 * Deliberately holds NO File object. The <input> element is the source of truth
 * for what the candidate picked, and the file is sent to the server the moment
 * it is chosen — so all this keeps is plain, serialisable data (name, size, the
 * stored URL, and a status). Previously a File lived in component state until
 * submit, and when something cleared that state the candidate still saw their
 * filename while the application went out with nothing attached.
 *
 * status: 'idle' | 'uploading' | 'uploaded' | 'failed'
 */
function useResumeUpload(screeningId, identityRef) {
  const [resume, setResume] = useState(EMPTY_RESUME);
  // Tracks the in-flight pre-upload so submit can await it instead of racing it.
  // A candidate who taps Continue while the upload is still running would
  // otherwise send the whole file a second time — the worst thing to do on the
  // slow connection that made the upload slow in the first place.
  const inFlightRef = useRef(null);

  const uploadResume = useCallback((file) => {
    if (!file || !screeningId) return Promise.resolve(EMPTY_RESUME);

    setResume({ name: file.name, sizeBytes: file.size, url: '', status: 'uploading' });

    const startedAt = Date.now();
    const run = async () => {
      // Force Android/Google Drive/iCloud to download and buffer into RAM before opening HTTP socket:
      let blob;
      try {
        blob = await materializeFileToBlob(file);
      } catch (matErr) {
        if (matErr?.message === 'FILE_EMPTY_0_BYTES') {
          reportDiag(screeningId, 'file_unreadable', { fileName: file.name, reason: 'empty_0_bytes' }, identityRef?.current);
        }
        throw matErr;
      }

      const fd = new FormData();
      fd.append('resume', blob || file, file.name);
      // Retried on transient network failure exactly like the /apply submit.
      // Without this a single dropped upload on a weak mobile uplink left the
      // resume unstored, so the candidate re-sent the whole file at submit —
      // doubling the bytes over the connection that had just failed.
      const res = await postMultipartWithRetry(
        `/api/screenings/${screeningId}/resume-upload`,
        fd,
        { screeningId, fileName: file.name, fileSizeKB: Math.round((blob?.size || file.size) / 1024) },
        (errInfo) =>
          reportDiag(
            screeningId,
            'preupload_attempt_failed',
            {
              fileName: file.name,
              fileSizeKB: Math.round((blob?.size || file.size) / 1024),
              attempt: errInfo.attempt,
              durationMs: errInfo.durationMs,
              sentPercent: errInfo.sentPercent,
              sentBytes: errInfo.sentBytes,
              uplinkKbps: errInfo.uplinkKbps,
              errorCode: errInfo.errorCode,
              errorMessage: errInfo.errorMessage,
              httpStatus: errInfo.httpStatus,
              isTimeout: errInfo.isTimeout,
              isNetworkFailure: errInfo.isNetworkFailure,
              wentOffline: errInfo.wentOffline,
            },
            identityRef?.current
          )
      );
      const url = res.data?.resumeUrl || '';
      const next = {
        name: res.data?.fileName || file.name,
        sizeBytes: blob?.size || file.size,
        url,
        status: url ? 'uploaded' : 'failed',
      };
      setResume(next);
      const elapsedMs = Date.now() - startedAt;
      reportDiag(
        screeningId,
        url ? 'preupload_ok' : 'preupload_no_url',
        {
          fileName: file.name,
          fileSizeKB: Math.round((blob?.size || file.size) / 1024),
          durationMs: elapsedMs,
          // Measured, not inferred. If slow connections really are the cause,
          // the successes will cluster high and the failures low — and if they
          // don't, this rules the theory out instead of confirming it.
          uplinkKbps: elapsedMs > 0 ? Math.round(((blob?.size || file.size) * 8) / elapsedMs) : null,
        },
        identityRef?.current
      );
      return next;
    };

    const promise = run().catch((err) => {
      // Left as 'failed' rather than surfaced as a blocking error: submit falls
      // back to reading the file straight from the input, so the candidate has
      // nothing to fix and should not be stopped here.
      console.warn('[APPLY_UPLOAD] Pre-upload failed; the file will be sent with the application instead', {
        screeningId,
        fileName: file.name,
        errorCode: err.code,
        httpStatus: err.response?.status,
      });
      // Terminal: every retry is spent. Reported separately from the per-attempt
      // events so a search can distinguish "recovered on retry" from "gave up".
      reportDiag(
        screeningId,
        'preupload_failed_final',
        {
          fileName: file.name,
          fileSizeKB: Math.round(file.size / 1024),
          totalDurationMs: Date.now() - startedAt,
          errorCode: err.code,
          errorMessage: err.message,
          httpStatus: err.response?.status,
        },
        identityRef?.current
      );
      const failed = { name: file.name, sizeBytes: file.size, url: '', status: 'failed' };
      setResume(failed);
      return failed;
    });

    inFlightRef.current = promise;
    // Always clear the slot this call owns — a later pick must not have its
    // promise wiped by an earlier, slower upload settling after it.
    promise.finally(() => {
      if (inFlightRef.current === promise) inFlightRef.current = null;
    });
    return promise;
  }, [screeningId]);

  /** Resolves to the final resume state once any in-flight pre-upload settles. */
  const waitForUpload = useCallback(() => inFlightRef.current || Promise.resolve(null), []);

  return { resume, setResume, uploadResume, waitForUpload };
}

const ScreeningResumeUpload = () => {
  const { screeningId } = useParams();
  const t = useCandidateLanguage({ screeningId });
  const location = useLocation();
  const navigate = useNavigate();
  const [basicDetails, setBasicDetails] = useState(() => location.state?.basicDetails || null);
  const [sessionHydrated, setSessionHydrated] = useState(!!location.state?.basicDetails);
  const [showReuploadHint, setShowReuploadHint] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const welcomeToastShownRef = useRef(false);
  const prevScreeningIdRef = useRef(screeningId);
  // One [APPLY_NO_FILE] report per page view — see handleSubmit.
  const noFileReportedRef = useRef(false);
  // Same one-shot guard for the bounce-to-step-one dead end, which renders on
  // every pass until navigation actually happens.
  const detailsLostReportedRef = useRef(false);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const handleBackAttempt = useCallback(() => setShowLeaveModal(true), []);
  const handleLeaveAnyway = useCallback(() => {
    setShowLeaveModal(false);
    navigate(-1);
  }, [navigate]);
  usePreventBack(true, { onBackAttempt: handleBackAttempt });
  useScreeningLeaveWarning(!!screeningId);
  // No session-end beacon here: leaving/resume page must not mark assessment completed.

  const [applyInfo, setApplyInfo] = useState(null);
  const [applyInfoLoading, setApplyInfoLoading] = useState(true);
  const [linkInactive, setLinkInactive] = useState(false);
  const [apiUnreachable, setApiUnreachable] = useState(false);
  // Held in a ref so diagnostics can always name the candidate, even from
  // callbacks created before basicDetails resolved (the upload hook is built
  // once, but must still report the right person on every event).
  const identityRef = useRef({ email: '', name: '' });
  identityRef.current = {
    email: basicDetails?.email || '',
    name: basicDetails?.name || '',
  };

  const { resume, setResume, uploadResume, waitForUpload } = useResumeUpload(screeningId, identityRef);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [extraDetails, setExtraDetails] = useState({
    noticePeriod: '',
    currentCtc: '',
    expectedCtc: '',
    age: '',
  });
  // Optional "transcripts available?" checkbox (transcripts_confirmation org flag). Unticked = No.
  const [transcriptsAvailable, setTranscriptsAvailable] = useState(false);
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(30 * 60); // 30 min — mobile users need enough time to find their file
  const fileInputRef = useRef(null);

  const [showProcessingOverlay, setShowProcessingOverlay] = useState(false);

  useScreeningExpiryRedirect({
    screeningId,
    email: basicDetails?.email || '',
    enabled: !!screeningId,
  });

  // Full-screen overlay only once submitting has taken 5 s.
  useEffect(() => {
    if (!submitting) {
      setShowProcessingOverlay(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowProcessingOverlay(true), 5000);
    return () => clearTimeout(timer);
  }, [submitting]);

  useEffect(() => {
    if (prevScreeningIdRef.current === screeningId) return;
    prevScreeningIdRef.current = screeningId;
    setDraftRestored(false);
    setExtraDetails({ noticePeriod: '', currentCtc: '', expectedCtc: '', age: '' });
    setResume(EMPTY_RESUME);
    setShowReuploadHint(false);
    welcomeToastShownRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [screeningId]);

  useEffect(() => {
    const fromNav = location.state?.basicDetails;
    if (fromNav) {
      setBasicDetails(fromNav);
      setSessionHydrated(true);
      saveResumeStageAfterBasic(screeningId, fromNav);
      return;
    }
    const s = readApplySession(screeningId);
    if (s?.basicDetails) setBasicDetails(s.basicDetails);
    setSessionHydrated(true);
  }, [screeningId, location.key, location.state?.basicDetails]);

  useEffect(() => {
    if (!basicDetails) return;
    const s = readApplySession(screeningId);
    if (s?.resumeDraft) {
      setExtraDetails((prev) => ({ ...prev, ...s.resumeDraft }));
    }
    const restoredFromDevice = !location.state?.basicDetails && !!s?.basicDetails;
    if (restoredFromDevice && !welcomeToastShownRef.current) {
      welcomeToastShownRef.current = true;
      toast.success(t('Welcome back — we restored your saved details on this device.'), { duration: 4500 });
    }
    setShowReuploadHint(!!s?.resumeFileMeta);
    setDraftRestored(true);
  }, [basicDetails, screeningId, location.state?.basicDetails, t]);

  useEffect(() => {
    if (!basicDetails || !screeningId || !draftRestored) return;
    patchResumeDraft(screeningId, extraDetails);
  }, [extraDetails, basicDetails, screeningId, draftRestored]);

  useEffect(() => {
    let cancelled = false;
    const fetchApplyInfo = async () => {
      try {
        const email = basicDetails?.email ? String(basicDetails.email).trim().toLowerCase() : '';
        const query = email ? `?email=${encodeURIComponent(email)}` : '';
        const res = await axios.get(`/api/screenings/${screeningId}/apply-info${query}`);
        if (!cancelled) {
          setApplyInfo(res.data);
          setLinkInactive(false);
        }
      } catch (e) {
        if (!cancelled) {
          setApplyInfo(null);
          const isInactive = e.response?.status === 410 && e.response?.data?.code === 'LINK_INACTIVE';
          setLinkInactive(!!isInactive);
          // No response at all means this device couldn't reach the API. The
          // upload will fail the same way, so say so up front rather than
          // letting the candidate fill everything in and hit a wall at submit.
          setApiUnreachable(!e.response);
          // Reaches the server only if the network recovers later; when it
          // never does, the absent report is itself the finding — paired with
          // the [BASIC_APPLY] line this candidate already produced, it shows
          // they got here and then lost the API entirely.
          reportDiag(
            screeningId,
            !e.response ? 'applyinfo_unreachable' : 'applyinfo_error',
            { httpStatus: e.response?.status, errorCode: e.code, errorMessage: e.message },
            { email: basicDetails?.email || '', name: basicDetails?.name || '' }
          );
        }
      } finally {
        if (!cancelled) setApplyInfoLoading(false);
      }
    };
    if (screeningId) fetchApplyInfo();
    return () => { cancelled = true; };
  }, [screeningId, basicDetails]);

  useEffect(() => {
    if (applyInfoLoading || !applyInfo) return;
    if (applyInfo.resumeCompleted) {
      const params = new URLSearchParams();
      if (basicDetails?.email) params.set('email', basicDetails.email.trim());
      if (basicDetails?.name) params.set('name', basicDetails.name.trim());
      
      // Document collection (if enabled) is handled from the hub after the
      // assessment/interview — the hub decides when to surface it.
      navigate(`/apply/${screeningId}/hub?${params.toString()}`, { replace: true });
    }
  }, [applyInfoLoading, applyInfo, basicDetails, screeningId, navigate]);

  const resumeEnabled = applyInfo?.hasResumeScoring !== false;

  const jobTitle = applyInfo?.jobTitle || 'this role';
  const screeningDeadline = applyInfo?.screeningDeadline || null;
  const timerExpired =
    !!applyInfo?.timeUp ||
    (screeningDeadline
      ? (() => {
          const parsed = Date.parse(screeningDeadline);
          return !Number.isNaN(parsed) && Date.now() >= parsed;
        })()
      : false);

  // Pre-start 10-minute timer, similar to Assessment/AI Interview pages.
  useEffect(() => {
    if (!applyInfo || timerExpired) return;
    const interval = setInterval(() => {
      setTimeLeftSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [applyInfo, timerExpired]);

  const localTimerExpired = timeLeftSeconds <= 0;

  useEffect(() => {
    if (localTimerExpired && !timerExpired) {
      toast.error(t('Your session expired. Please confirm your details to restart.'), { id: 'local-timeout' });
      if (basicDetails?.email) {
        axios.post(`/api/screenings/${screeningId}/local-timeout`, { email: basicDetails.email }).catch(err => console.error('Failed to report local timeout:', err));
      }
      navigate(`/apply/${screeningId}`, { replace: true });
    }
  }, [localTimerExpired, timerExpired, screeningId, navigate, basicDetails, t]);

  // Page-level lifecycle. Each of these is a way a candidate can fail that
  // leaves no other trace: the page never became usable, the OS suspended the
  // tab mid-upload (very common when the picker sends them to another app),
  // the radio dropped, or they closed the tab while bytes were in flight.
  const resumeStatusRef = useRef(resume.status);
  resumeStatusRef.current = resume.status;
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;

  useEffect(() => {
    if (!screeningId) return undefined;
    reportDiag(screeningId, 'page_open', { ...readCapabilityInfo(), referrer: document.referrer || 'none' }, identityRef.current);

    // A hard JS error or an unhandled rejection on a specific device leaves the
    // candidate on a broken page and leaves us with nothing. These two are the
    // only way that class of failure becomes visible.
    const onError = (ev) =>
      reportDiag(
        screeningId,
        'js_error',
        {
          message: String(ev?.message || 'unknown').slice(0, 200),
          source: String(ev?.filename || '').slice(-80),
          line: ev?.lineno,
          col: ev?.colno,
        },
        identityRef.current
      );
    const onRejection = (ev) =>
      reportDiag(
        screeningId,
        'unhandled_rejection',
        { reason: String(ev?.reason?.message || ev?.reason || 'unknown').slice(0, 200) },
        identityRef.current
      );

    const onOffline = () =>
      reportDiag(screeningId, 'connection_offline', { resumeStatus: resumeStatusRef.current, submitting: submittingRef.current }, identityRef.current);
    const onOnline = () =>
      reportDiag(screeningId, 'connection_online', { resumeStatus: resumeStatusRef.current }, identityRef.current);

    const onVisibility = () => {
      // Only interesting while work is in flight — a backgrounded tab is where
      // mobile browsers throttle or kill an in-progress request.
      if (document.visibilityState !== 'hidden') return;
      if (resumeStatusRef.current !== 'uploading' && !submittingRef.current) return;
      reportDiag(
        screeningId,
        'hidden_during_upload',
        { resumeStatus: resumeStatusRef.current, submitting: submittingRef.current },
        identityRef.current,
        { beacon: true }
      );
    };

    const onPageHide = () => {
      if (resumeStatusRef.current !== 'uploading' && !submittingRef.current) return;
      reportDiag(
        screeningId,
        'abandoned_during_upload',
        { resumeStatus: resumeStatusRef.current, submitting: submittingRef.current },
        identityRef.current,
        { beacon: true }
      );
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [screeningId]);

  const handleFileChange = useCallback((file) => {
    if (!file) {
      setResume(EMPTY_RESUME);
      setResumeFileMeta(screeningId, null);
      return;
    }
    setShowReuploadHint(false);
    setResumeFileMeta(screeningId, file);
    reportDiag(
      screeningId,
      'file_picked',
      {
        fileName: file.name,
        fileSizeKB: Math.round(file.size / 1024),
        fileType: file.type || 'none',
        lastModified: file.lastModified || null,
        // A 0-byte pick is the signature of a file that lives in Drive/iCloud
        // and was never materialised locally.
        emptyFile: file.size === 0,
      },
      identityRef.current
    );
    // Confirm the bytes are actually readable. A File handle can be returned by
    // the picker and still fail to read — the file was moved, the cloud
    // provider never downloaded it, or the WebView lost storage permission. In
    // every one of those cases the candidate sees their filename on screen and
    // has no idea anything is wrong, so read a slice and say so if it fails.
    try {
      const probe = file.slice(0, Math.min(1024, file.size || 1));
      if (typeof probe.arrayBuffer === 'function') {
        probe
          .arrayBuffer()
          .then((buf) => {
            if (!buf || buf.byteLength === 0) {
              reportDiag(screeningId, 'file_unreadable', { fileName: file.name, reason: 'empty_slice' }, identityRef.current);
            }
          })
          .catch((readErr) => {
            reportDiag(
              screeningId,
              'file_read_error',
              { fileName: file.name, errorName: readErr?.name, errorMessage: readErr?.message },
              identityRef.current
            );
          });
      }
    } catch (_) {
      /* probe is best-effort only */
    }
    // A 0-byte pick is common on mobile when the file lives in cloud storage and
    // hasn't been downloaded locally. Warn, but never block.
    setError(
      file.size === 0
        ? "This file looks empty (0 KB). If it's stored in Google Drive or iCloud, downloading it to your device first usually fixes it — but you can still continue."
        : ''
    );
    uploadResume(file);
  }, [screeningId, uploadResume]);

  // Counts taps on the drop zone against files actually returned. A candidate
  // whose picker never opens taps repeatedly and produces zero 'file_picked'
  // events — the two numbers together turn "it doesn't work" into a fact.
  const pickerOpenCountRef = useRef(0);
  const lastPickerOpenAtRef = useRef(0);

  const handlePickerOpen = useCallback(() => {
    pickerOpenCountRef.current += 1;
    lastPickerOpenAtRef.current = Date.now();
    reportDiag(
      screeningId,
      'picker_opened',
      { attemptNumber: pickerOpenCountRef.current },
      identityRef.current
    );
    fileInputRef.current?.click();
  }, [screeningId]);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      // The picker returned but handed back nothing — the candidate cancelled,
      // or the picker closed itself because an `accept` filter or a permission
      // left them nothing selectable. Worth distinguishing from never opening.
      reportDiag(
        screeningId,
        'picker_returned_no_file',
        { msSinceOpen: lastPickerOpenAtRef.current ? Date.now() - lastPickerOpenAtRef.current : null },
        identityRef.current
      );
      return;
    }
    handleFileChange(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    // Dropped files never reach the <input>, so put them there — the input is
    // the source of truth for what the candidate has selected.
    if (fileInputRef.current && typeof DataTransfer !== 'undefined') {
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        fileInputRef.current.files = dt.files;
      } catch (_) {
        // Older browsers disallow assigning input.files; the upload below still
        // runs, so the drop is not lost.
      }
    }
    handleFileChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleExtraChange = (e) => {
    const { name, value } = e.target;
    setExtraDetails((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side gating is deliberately minimal here: when a candidate is
    // blocked in the browser there is no server-side record of it, which left
    // us unable to diagnose failures. Deadline/file-type/required-field checks
    // are all enforced (and logged) by the backend instead. Only conditions the
    // server genuinely cannot act on are still checked here.
    if (!basicDetails) {
      setError('Your basic details were not found. Please go back and fill them again.');
      return;
    }

    setSubmitting(true);
    setError('');

    const submitStartedAt = Date.now();
    // Read straight from the input: it is the only place a File is held, so it
    // cannot be out of sync with what the candidate sees.
    const domFile = fileInputRef.current?.files?.[0] || null;

    // If the pre-upload is still running, wait for it rather than racing it.
    // Sending the bytes again here is exactly what we must avoid on the weak
    // connections where this matters. `resume` is a snapshot from render and
    // can be stale by now, so trust the settled result when we get one.
    let settled = (await waitForUpload()) || resume;

    // If pre-upload failed earlier or is missing URL, and we have a local domFile,
    // auto-retry the pre-upload once on Submit so we can send a tiny ~1KB payload instead of raw bytes!
    if (resumeEnabled && !settled.url && domFile && domFile.size > 0) {
      try {
        const retryPreupload = await uploadResume(domFile);
        if (retryPreupload?.url) {
          settled = retryPreupload;
        }
      } catch (_) {
        // Fall back to inline if pre-upload still fails
      }
    }

    // Nothing attached at all: no stored URL, no file on the input, and the
    // uploader never even left 'idle' (so the picker never returned a file).
    // The server rejects this, and it was doing so *after* a full multipart
    // round-trip on a slow connection — the candidate waited, then got an
    // error. Tell them immediately instead, but still report it so the
    // [APPLY_NO_FILE] server-side picture stays complete.
    if (resumeEnabled && !settled.url && !domFile) {
      setError(
        'Please attach your resume before continuing. If tapping the upload box does nothing, try opening this link in Chrome, or reply to the recruiter to send your resume by email.'
      );
      setSubmitting(false);
      console.warn('[APPLY_UPLOAD] Blocked submit with no resume attached', {
        screeningId,
        email: basicDetails.email,
        status: settled.status,
      });
      // Report once per page view. A candidate whose picker never opens taps
      // Continue repeatedly out of frustration — one candidate produced ~50 of
      // these in 30 seconds, which buries every other log line and tells us
      // nothing the first report didn't.
      if (!noFileReportedRef.current) {
        noFileReportedRef.current = true;
        axios
          .post(`/api/screenings/${screeningId}/apply-no-file`, {
            email: basicDetails.email,
            clientFileState: {
              status: settled.status,
              // Both are necessarily false to have reached this branch, but
              // state them as measurements rather than constants so the log
              // never claims to have observed something it didn't.
              hasInputFile: !!domFile,
              preUploaded: !!settled.url,
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            },
          })
          .catch(() => {
            /* diagnostics only — never block the candidate on this */
          });
        // Also recorded on the richer channel: how many times they tapped the
        // upload box before giving up separates "picker never opened" from
        // "never tried", and the capability/context fields say which browser
        // they were fighting.
        reportDiag(
          screeningId,
          'submit_blocked_no_file',
          {
            resumeStatus: settled.status,
            pickerOpens: pickerOpenCountRef.current,
            hasInputFile: !!domFile,
            ...readCapabilityInfo(),
          },
          identityRef.current
        );
      }
      return;
    }

    const logContext = {
      screeningId,
      email: basicDetails.email,
      fileName: settled.name || domFile?.name,
      fileSizeKB: Math.round((settled.sizeBytes || domFile?.size || 0) / 1024),
    };

    // Recorded before the request goes out, so a submit that dies mid-body —
    // leaving no server-side trace at all — is still visible as a start with no
    // matching outcome. That silent case is the one we could not see before.
    reportDiag(
      screeningId,
      'submit_start',
      {
        fileName: logContext.fileName,
        fileSizeKB: logContext.fileSizeKB,
        resumeStatus: settled.status,
        // The critical branch: a stored URL sends ~1KB of JSON, while the
        // fallback re-sends the whole file over a connection that already
        // failed once.
        carrier: settled.url ? 'preuploaded_url' : 'inline_file',
        hasInputFile: !!domFile,
        pickerOpens: pickerOpenCountRef.current,
        secondsLeftOnTimer: timeLeftSeconds,
      },
      identityRef.current
    );

    try {
      const data = new FormData();
      data.append('name', basicDetails.name);
      data.append('email', basicDetails.email);
      data.append('phone', basicDetails.phone || '');

      // Appended BEFORE the file: multer only exposes fields in req.body that
      // were parsed ahead of the file part, and this must stay readable even
      // when no file is attached.
      data.append(
        'clientFileState',
        JSON.stringify({
          status: settled.status,
          name: settled.name || null,
          size: settled.sizeBytes || null,
          preUploaded: !!settled.url,
          hasInputFile: !!domFile,
          resumeEnabled,
          applyInfoLoaded: !!applyInfo,
        })
      );
      // Also before the file, for the same multer reason. The server decides whether it applies.
      data.append('transcriptsAvailable', transcriptsAvailable ? 'true' : 'false');

      if (settled.url) {
        // Stored when the file was picked — send the reference. A string cannot
        // go missing the way the File object did.
        data.append('resumeUrl', settled.url);
        data.append('resumeFileName', settled.name || domFile?.name || 'resume.pdf');
      } else if (domFile) {
        // Pre-upload genuinely failed — send the bytes as the fallback.
        let uploadBlob = domFile;
        try {
          uploadBlob = (await materializeFileToBlob(domFile)) || domFile;
        } catch (_) {}
        data.append('resume', uploadBlob, domFile.name);
      }
      if (basicDetails.storeId) data.append('storeId', basicDetails.storeId);
      if (basicDetails.customFields && Object.keys(basicDetails.customFields).length > 0) {
        data.append('customFields', JSON.stringify(basicDetails.customFields));
      }

      const res = await postMultipartWithRetry(
        `/api/screenings/${screeningId}/apply`,
        data,
        logContext,
        (errInfo) =>
          reportDiag(
            screeningId,
            'submit_attempt_failed',
            {
              attempt: errInfo.attempt,
              durationMs: errInfo.durationMs,
              sentPercent: errInfo.sentPercent,
              sentBytes: errInfo.sentBytes,
              totalBytes: errInfo.totalBytes,
              uplinkKbps: errInfo.uplinkKbps,
              errorCode: errInfo.errorCode,
              errorMessage: errInfo.errorMessage,
              httpStatus: errInfo.httpStatus,
              isTimeout: errInfo.isTimeout,
              isNetworkFailure: errInfo.isNetworkFailure,
              wentOffline: errInfo.wentOffline,
              carrier: settled.url ? 'preuploaded_url' : 'inline_file',
            },
            identityRef.current
          )
      );
      console.info('[APPLY_UPLOAD] Submission succeeded', {
        ...logContext,
        totalDurationMs: Date.now() - submitStartedAt,
      });
      reportDiag(
        screeningId,
        'submit_ok',
        { totalDurationMs: Date.now() - submitStartedAt, carrier: settled.url ? 'preuploaded_url' : 'inline_file' },
        identityRef.current
      );

      const { assessmentSharingLink, interviewSharingLink, qualified } = res.data || {};

      if (qualified === false) {
        clearApplySession(screeningId);
        const thankYouParams = new URLSearchParams();
        if (basicDetails.email) thankYouParams.set('email', basicDetails.email.trim());
        if (basicDetails.name) thankYouParams.set('name', basicDetails.name.trim());
        thankYouParams.set('rejected', 'true');
        const qs = thankYouParams.toString();
        navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
        return;
      }

      markHubStage(screeningId, basicDetails.email, basicDetails.name);

      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

      const params = new URLSearchParams();
      const email = basicDetails.email || '';
      const name = basicDetails.name || '';
      const hasEmail = email.trim().length > 0;
      const hasName = name.trim().length > 0;
      if (hasEmail) params.set('email', email.trim());
      if (hasName) params.set('name', name.trim());

      // Build returnUrl back to hub so AI interview (and other flows) can bring the
      // candidate to the screening hub when they are done.
      const hubQuery = params.toString();
      const hubPath = `/apply/${screeningId}/hub${hubQuery ? `?${hubQuery}` : ''}`;
      const hubReturnUrl = baseUrl + hubPath;

      // Document collection (if enabled) happens last, from the hub after the
      // assessment/interview — never route candidates there first.

      // Decide the immediate next step:
      // - If Document Collection is enabled, send them there.
      // - Otherwise, if Assessment AND Interview are configured, send them to Assessment.
      // - If only Assessment, send them to Assessment.
      // - If only Interview, send them to Interview.
      const isDocumentCollectionPending = applyInfo?.includeDocumentCollection;
      const documentCollectionToken = res.data?.documentCollectionToken;

      if (isDocumentCollectionPending && documentCollectionToken) {
        const docParams = new URLSearchParams(params);
        docParams.set('token', documentCollectionToken);
        navigate(`/apply/${screeningId}/documents?${docParams.toString()}`);
      } else if (res.data?.applicationFormToken) {
        navigate(applicationFormPath(screeningId, res.data.applicationFormToken, { email, name }));
      } else if (assessmentSharingLink) {
        let assessmentReturnUrl = hubReturnUrl;

        if (interviewSharingLink) {
          const interviewQuery = new URLSearchParams();
          interviewQuery.set('returnUrl', hubReturnUrl);
          interviewQuery.set('screeningId', screeningId);
          if (hasEmail) interviewQuery.set('email', email.trim());
          if (hasName) interviewQuery.set('name', name.trim());
          interviewQuery.set('hasAssessment', 'true');
          const interviewUrl = `/ai-interview/${interviewSharingLink}?${interviewQuery.toString()}`;
          assessmentReturnUrl = baseUrl + interviewUrl;
        }

        const assessmentQuery = new URLSearchParams();
        assessmentQuery.set('returnUrl', assessmentReturnUrl);
        assessmentQuery.set('screeningId', screeningId);
        if (hasEmail) assessmentQuery.set('email', email.trim());
        if (hasName) assessmentQuery.set('name', name.trim());
        if (interviewSharingLink) {
          assessmentQuery.set('hasInterview', 'true');
          assessmentQuery.set('interviewSharingLink', interviewSharingLink);
        }
        const assessmentUrl = `/assessment/${assessmentSharingLink}?${assessmentQuery.toString()}`;
        navigate(assessmentUrl);
      } else if (interviewSharingLink) {
        const interviewQuery = new URLSearchParams();
        interviewQuery.set('returnUrl', hubReturnUrl);
        interviewQuery.set('screeningId', screeningId);
        if (hasEmail) interviewQuery.set('email', email.trim());
        if (hasName) interviewQuery.set('name', name.trim());
        const interviewUrl = `/ai-interview/${interviewSharingLink}?${interviewQuery.toString()}`;
        navigate(interviewUrl);
      } else {
        const thankYouParams = new URLSearchParams();
        if (hasEmail) thankYouParams.set('email', email.trim());
        if (hasName) thankYouParams.set('name', name.trim());
        const qs = thankYouParams.toString();
        navigate(`/apply/${screeningId}/thank-you${qs ? `?${qs}` : ''}`, { replace: true });
      }
    } catch (err) {
      const serverMsg = err.response?.data?.message;
      const isNetworkFailure = !err.response;
      const errorMsg = serverMsg
        || (isNetworkFailure
          ? 'We couldn’t reach the server. Please check your connection and try uploading again.'
          : 'Failed to submit your application. Please try again in a moment.');
      setError(errorMsg);
      console.error('[APPLY_UPLOAD] Submission failed', {
        ...logContext,
        totalDurationMs: Date.now() - submitStartedAt,
        isNetworkFailure,
        isTimeout: err.code === 'ECONNABORTED',
        errorCode: err.code,
        errorMessage: err.message,
        httpStatus: err.response?.status,
        responseBody: err.response?.data,
      });
      // The banner the candidate is looking at, recorded server-side. This is
      // the event that was previously invisible: when the socket dies mid-body
      // the request never reaches Express, so nothing else in the stack knows
      // this candidate exists. Sent as a beacon because a candidate who has
      // just seen an error often closes the tab immediately.
      reportDiag(
        screeningId,
        'submit_failed_final',
        {
          fileName: logContext.fileName,
          fileSizeKB: logContext.fileSizeKB,
          totalDurationMs: Date.now() - submitStartedAt,
          isNetworkFailure,
          isTimeout: err.code === 'ECONNABORTED',
          errorCode: err.code,
          errorMessage: err.message,
          httpStatus: err.response?.status,
          carrier: settled.url ? 'preuploaded_url' : 'inline_file',
          shownToCandidate: errorMsg,
        },
        identityRef.current,
        { beacon: true }
      );
    } finally {
      setSubmitting(false);
    }
  };

  // If resume scoring is disabled this page should never be shown. Redirect to basic apply page.
  if (!applyInfoLoading && applyInfo?.hasResumeScoring === false) {
    navigate(`/apply/${screeningId}`, { replace: true });
    return null;
  }

  // Dead end: the candidate reached this page but their details could not be
  // recovered, so they are sent back to step one. Storage being blocked is the
  // usual cause and it is entirely invisible server-side — the candidate just
  // appears to restart, exactly the four-restart pattern seen in the logs.
  if (sessionHydrated && !basicDetails && !applyInfoLoading && !detailsLostReportedRef.current) {
    detailsLostReportedRef.current = true;
    reportDiag(
      screeningId,
      'details_lost_bounced_to_step1',
      { hadNavState: !!location.state?.basicDetails, ...readCapabilityInfo() },
      identityRef.current
    );
  }

  if (sessionHydrated && !basicDetails && !applyInfoLoading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
          <h1 className="text-lg font-semibold text-gray-900">{t('Start from the apply page')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t("We couldn't find your details on this device. Enter your information again from step one, or use the same browser you started with.")}
          </p>
          <Link
            to={`/apply/${screeningId}`}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-electric-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-electric-blue-600"
          >
            {t('Go to basic details')}
          </Link>
        </div>
      </div>
    );
  }

  if (linkInactive && !applyInfoLoading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
            <LinkIcon className="h-6 w-6 text-amber-600" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{t('This application link is no longer active')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('The recruiter has disabled this link, so applications are no longer being accepted. If you believe this is an error, please contact the recruiter or company directly.')}
          </p>
        </div>
      </div>
    );
  }

  const step2Label = resumeEnabled ? 'Resume upload' : 'Additional details';
  const steps = [
    'Basic details',
    step2Label,
    ...(applyInfo?.includeDocumentCollection ? ['Document upload'] : []),
    ...(applyInfo?.includeApplicationForm ? ['Application form'] : []),
    ...(basicDetails?.hasAssessment ? ['Assessment'] : []),
    ...(basicDetails?.hasInterview ? ['AI interview'] : []),
  ];
  const currentStepIndex = 1;
  const interviewType = applyInfo?.interviewType || 'voice';
  const STEP_META = getStepMeta(interviewType);

  // Wait for applyInfo to load before rendering the form so that
  // resumeEnabled is resolved and the UI doesn't flash/glitch.
  if (applyInfoLoading) {
    return (
      <div className="min-h-screen bg-white-smoke flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-electric-blue" />
          <p className="text-sm text-gray-500">{t('Loading…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white-smoke px-4 py-4 sm:py-6 md:py-8">
      {/* Full-screen Processing Overlay */}
      {showProcessingOverlay && (
        <ResumeProcessingOverlay
          title="Processing your resume"
          messages={PROCESSING_MESSAGES}
          note={<>{t('This may take a few minutes depending on the document size.')}<br />{t('Please do not close this window.')}</>}
        />
      )}

      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={handleLeaveAnyway}
        title="Leave this page?"
        message="Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out."
      />
      <div className="mx-auto max-w-6xl">
        {/* Header: compact on mobile, full on desktop */}
        <header className="flex items-center justify-between gap-3">
          <div className="flex flex-col min-w-0">
            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {t('Powered by')}
            </p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <img
                src="/logo.svg"
                alt="TalentCrux"
                className="h-9 w-9 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain flex-shrink-0"
              />
              <span className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">TalentCrux</span>
            </div>
          </div>
          <div className="hidden sm:flex items-center justify-end flex-shrink-0 min-w-0" aria-hidden />
        </header>

        {/* Process Timeline – match basic details page */}
        {steps.length > 0 && (
          <section className="mt-6 sm:mt-8">
            <p className="text-center text-[11px] sm:text-sm font-semibold uppercase tracking-[0.22em] text-gray-500 mb-5 sm:mb-7">
              {t('Your screening journey')}
            </p>
            <div className="flex items-start justify-center gap-0">
              {steps.map((label, index) => {
                const meta = STEP_META[label] || { icon: CheckIcon, desc: STEP_SECONDARY[label] || '', gradient: 'from-gray-400 to-gray-500' };
                const StepIcon = meta.icon;
                const isActive = index === currentStepIndex;
                const isCompleted = index < currentStepIndex;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center text-center min-w-0 flex-1 sm:flex-none sm:w-32">
                      <span
                        className={`text-[9px] sm:text-xs font-semibold uppercase tracking-[0.06em] sm:tracking-[0.16em] whitespace-nowrap mb-1.5 ${
                          isActive ? 'text-electric-blue' : isCompleted ? 'text-emerald-400' : 'text-gray-300'
                        }`}
                      >
                        {t('Step {n}', { n: index + 1 })}
                      </span>
                      <div
                        className={`relative flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl transition-all duration-500 ${
                          isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white shadow-lg`
                            : isCompleted
                            ? 'bg-emerald-50 text-emerald-500 border-2 border-emerald-200'
                            : 'bg-white text-gray-300 border-2 border-gray-200 border-dashed'
                        }`}
                        style={
                          isActive
                            ? { boxShadow: '0 8px 25px rgba(0, 123, 255, 0.2)' }
                            : isCompleted
                            ? { boxShadow: '0 6px 18px rgba(16, 185, 129, 0.18)' }
                            : {}
                        }
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.5} />
                        ) : (
                          <StepIcon className="h-5 w-5 sm:h-7 sm:w-7" strokeWidth={isActive ? 2 : 1.5} />
                        )}
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-2xl border-2 border-electric-blue/30 animate-ping"
                            style={{ animationDuration: '2s' }}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-2 text-[10px] sm:text-sm font-semibold leading-tight hyphens-auto [overflow-wrap:anywhere] ${
                          isActive ? 'text-gray-900' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                        }`}
                      >
                        {t(label === 'AI interview' ? getInterviewStepLabel(interviewType) : label)}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="flex-1 flex items-center pt-[52px] sm:pt-[52px] min-w-[6px] max-w-[12px] sm:max-w-[72px]">
                        <div className="w-full flex items-center gap-0.5">
                          {[...Array(4)].map((_, i) => (
                            <div
                              // eslint-disable-next-line react/no-array-index-key
                              key={i}
                              className={`flex-1 h-0.5 rounded-full ${
                                index < currentStepIndex ? 'bg-emerald-400/60' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </section>
        )}

        {/* Main card – aligned with pre-assessment style */}
        <div className="mt-4 sm:mt-8 max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
            {/* Header strip */}
            <div className="bg-electric-blue px-5 py-4 sm:px-6 sm:py-5">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                    {resumeEnabled ? t('Upload your resume') : t('Additional details')}
                  </h2>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 sm:px-3.5 sm:py-1.5 text-[10px] sm:text-xs font-semibold text-electric-blue-700 shadow-sm whitespace-nowrap">
                    <ClockIcon className="h-4 w-4 text-electric-blue-500" />
                    <span>
                      {timerExpired
                        ? t('Time up – window closed')
                        : t('Upload within {time}', {
                            time: `${Math.floor(timeLeftSeconds / 60)
                              .toString()
                              .padStart(2, '0')}:${(timeLeftSeconds % 60)
                              .toString()
                              .padStart(2, '0')}`,
                          })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-4 sm:p-6 lg:p-8">
              <p className="text-xs text-gray-600 sm:text-sm">
                {resumeEnabled
                  ? t('Upload your resume to continue.')
                  : t('Review your details to continue.')}
              </p>

              {apiUnreachable && (
                <div
                  className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800 sm:text-sm"
                  role="alert"
                >
                  <p className="font-semibold">{t("We can't connect to our servers from this browser.")}</p>
                  <p className="mt-1">
                    {t('If you opened this link from WhatsApp or another app, please copy the link and open it directly in Chrome or Safari. Switching between WiFi and mobile data can also help.')}
                  </p>
                </div>
              )}

              {resumeEnabled && showReuploadHint && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs text-amber-900 sm:text-sm">
                  {t("You had a resume selected earlier on this device. Browsers can't keep the file after a refresh — please attach your resume again to continue.")}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="mt-5 space-y-5 sm:mt-6 sm:space-y-6"
                aria-describedby={error ? 'resume-form-error' : undefined}
              >
                <div className={`grid gap-5 sm:gap-6 ${resumeEnabled ? '' : ''}`}>
                  {/* Left column – required details */}
                  

                  {/* Right column – resume upload */}
                  {resumeEnabled && (
                    <div className="w-full min-w-0 rounded-2xl border border-gray-100 bg-slate-50/70 px-4 py-4 sm:px-5 sm:py-5">
                      {/*
                        No htmlFor: the drop zone below owns opening the picker.
                        Pointing this at the input too would give some browsers a
                        second forwarding path and open the picker twice.
                      */}
                      <label
                        id="resume-input-label"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t('Resume')} <span className="text-red-500">*</span>{' '}
                        <span className="text-gray-400 font-normal">(PDF, DOC, DOCX, JPG, PNG)</span>
                      </label>
                      {/*
                        Deliberately a div, not a <label htmlFor>. Relying on the
                        browser to forward a label click to the input is what
                        failed on Android WebViews (HeyTapBrowser/Oppo), and
                        combining a label with an explicit .click() opens the
                        picker twice on browsers where forwarding *does* work —
                        the forwarded click bubbles back up to the label. Owning
                        the click here is the one behaviour that is identical
                        everywhere. Keyboard and screen-reader access are
                        preserved via role/tabIndex/aria-labelledby + Enter/Space.
                      */}
                      <div
                        role="button"
                        tabIndex={0}
                        aria-labelledby="resume-input-label"
                        aria-describedby="resume-input-hint"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onClick={handlePickerOpen}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePickerOpen();
                          }
                        }}
                        className="flex w-full min-w-0 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-white px-4 py-6 text-center hover:border-electric-blue hover:bg-electric-blue-50/40 transition-colors min-h-[160px] sm:min-h-[200px] sm:py-10"
                      >
                        <input
                          id="resume-input"
                          ref={fileInputRef}
                          type="file"
                          // No `accept` filter. Some Android file pickers honour
                          // it by greying out everything the candidate has —
                          // documents in Drive, a photo of a printed CV — so the
                          // picker opens but nothing can be selected and it
                          // closes with no file, which looks identical to the
                          // picker never opening. The server already accepts
                          // unrecognised types and lets the parser decide, so
                          // filtering here only costs us applications.
                          onChange={handleInputChange}
                          // NOT `hidden`/display:none. A display:none input has no
                          // layout box, and Android WebViews (WhatsApp/in-app
                          // browsers, Samsung Internet) refuse to open the OS file
                          // picker for a label-forwarded click on an element with
                          // no layout box — the candidate taps and nothing happens,
                          // silently. `sr-only` keeps it visually hidden but in the
                          // layout tree, which is also the standard accessible
                          // pattern for custom file inputs.
                          className="sr-only"
                          data-testid="resume-upload-input"
                        />
                        {resume.name ? (
                          <>
                            <div className="w-full min-w-0 max-w-md rounded-xl border border-gray-200 bg-slate-50/70 px-3 py-2 text-left shadow-sm">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-electric-blue-50 flex-shrink-0">
                                    <DocumentTextIcon className="h-4 w-4 text-electric-blue" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="truncate text-xs font-semibold text-gray-900 sm:text-sm">
                                      {resume.name}
                                    </p>
                                    <p className="text-[11px] text-gray-500">
                                      {t('{size} MB', { size: (resume.sizeBytes / 1024 / 1024).toFixed(1) })}
                                      {resume.status === 'uploading' && (
                                        <span className="ml-2 text-electric-blue">{t('Uploading…')}</span>
                                      )}
                                      {resume.status === 'uploaded' && (
                                        <span className="ml-2 text-emerald-600">{t('Uploaded')}</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setResume(EMPTY_RESUME);
                                    fileInputRef.current && (fileInputRef.current.value = '');
                                  }}
                                  className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-700 text-xs flex-shrink-0"
                                  aria-label={t('Remove file')}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                            <span className="mt-2 text-[11px] text-gray-500 sm:text-xs">
                              {t('Click anywhere in this box or drag a different file to replace the uploaded resume.')}
                            </span>
                            <span className="mt-2 inline-flex items-center rounded-full bg-electric-blue-50 px-3 py-1 text-[11px] font-medium text-electric-blue-800">
                              {t('Supported: PDF, DOC, DOCX, JPG, PNG · Max 10MB')}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs font-semibold text-gray-900 sm:text-sm">
                              {t('Drag & drop your resume here')}
                            </span>
                            <span className="mt-1 text-xs text-gray-500 sm:text-sm">
                              {t('or click to browse a file from your device')}
                            </span>
                            <span
                              id="resume-input-hint"
                              className="mt-2 inline-flex items-center rounded-full bg-electric-blue-50 px-3 py-1 text-[11px] font-medium text-electric-blue-800"
                            >
                              {t('Supported: PDF, DOC, DOCX, JPG, PNG · Max 10MB')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {resumeEnabled && applyInfo?.transcriptsConfirmationEnabled === true && (
                  <div className="flex items-center gap-3 px-1">
                    <input
                      type="checkbox"
                      id="transcripts-available"
                      data-testid="transcripts-available-checkbox"
                      checked={transcriptsAvailable}
                      onChange={(e) => setTranscriptsAvailable(e.target.checked)}
                      className="h-4 w-4 flex-shrink-0 rounded border-gray-300 text-electric-blue focus:ring-electric-blue"
                    />
                    <label
                      htmlFor="transcripts-available"
                      className="text-xs text-gray-800 cursor-pointer select-none leading-snug font-medium sm:text-sm"
                    >
                      {t('Are transcripts available for each semester/year?')}
                    </label>
                  </div>
                )}

                {error && (
                  <div
                    id="resume-form-error"
                    className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
                    role="alert"
                  >
                    {t(error)}
                  </div>
                )}

                <div className="flex flex-row items-center justify-end gap-3 pt-4 mt-4 border-t border-gray-100 sm:gap-4 sm:pt-6 sm:mt-6">
                  <button
                    type="submit"
                    data-testid="resume-submit-btn"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-electric-blue to-indigo-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-electric-blue/20 hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-electric-blue focus:ring-offset-2 focus:ring-offset-white disabled:opacity-60 disabled:cursor-not-allowed transition-all sm:py-3"
                    aria-busy={submitting}
                    aria-live="polite"
                  >
                     {timerExpired
                      ? t("Time's up")
                      : submitting
                      ? t('Submitting…')
                      : (basicDetails?.hasAssessment || basicDetails?.hasInterview || applyInfo?.includeDocumentCollection)
                      ? t('Continue to screening')
                      : t('Finish')}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreeningResumeUpload;
